import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { LogoMark } from "./Brand";
import InstallButton from "./InstallButton";
import VoteBanner from "./VoteBanner";
import { scrollTo } from "@/lib/motion";

const LINKS = [
  { label: "Episodes", target: "#episodes" },
  { label: "About", target: "#about" },
  { label: "Host", target: "#hosts" },
  { label: "Lowdown", target: "/lowdown" },
  { label: "Morning", target: "/morning" },
  { label: "Dear Glowgetters", target: "/glowgetters" },
  { label: "Rhythm Report", target: "/rhythm" },
  { label: "Contact", target: "#contact" },
];

export default function NavigationHeader() {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  const go = (target) => {
    setOpen(false);
    if (typeof target === "number") {
      if (window.location.pathname !== "/") {
        navigate("/");
        window.scrollTo(0, 0);
        return;
      }
      scrollTo(target);
      return;
    }
    if (target.startsWith("/")) {
      navigate(target);
      window.scrollTo(0, 0);
      return;
    }
    if (window.location.pathname !== "/") {
      navigate("/");
      setTimeout(() => scrollTo(target), 350);
      return;
    }
    setTimeout(() => scrollTo(target), open ? 150 : 0);
  };

  return (
    <header className="fixed inset-x-0 top-0 z-50">
      <VoteBanner />
      <div className="glass border-b border-white/8">
        <div className="mx-auto flex h-[72px] max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
          <button
            data-testid="nav-logo"
            onClick={() => go(0)}
            className="flex items-center gap-3 transition-opacity hover:opacity-80"
          >
            <LogoMark className="h-8 w-8" />
            <span className="font-display text-sm font-bold uppercase tracking-[0.14em] sm:text-base">
              City Girls <span className="text-cherry">Decoded</span>
            </span>
          </button>

          <nav className="hidden items-center gap-6 xl:flex">
            {LINKS.map((l) => (
              <button
                key={l.target}
                data-testid={`nav-link-${l.target.slice(1)}`}
                onClick={() => go(l.target)}
                className="whitespace-nowrap text-xs font-semibold uppercase tracking-[0.18em] text-white/60 transition-colors hover:text-cherry"
              >
                {l.label}
              </button>
            ))}
            <InstallButton variant="pill" />
            <button
              data-testid="nav-listen-btn"
              onClick={() => go("#platforms")}
              className="rounded-full bg-cherry px-5 py-2.5 text-xs font-bold uppercase tracking-[0.14em] text-white transition-transform duration-300 hover:scale-105 hover:bg-cherry-soft"
            >
              Listen Now
            </button>
          </nav>

          <button
            data-testid="nav-mobile-toggle"
            className="rounded-full border border-white/15 p-2.5 text-white xl:hidden"
            onClick={() => setOpen((v) => !v)}
            aria-label="Toggle menu"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.25 }}
            className="glass border-b border-white/10 px-6 py-6 xl:hidden"
          >
            <div className="flex flex-col gap-5">
              {LINKS.map((l) => (
                <button
                  key={l.target}
                  data-testid={`nav-mobile-${l.target.slice(1)}`}
                  onClick={() => go(l.target)}
                  className="text-left font-display text-2xl font-bold uppercase text-white/90"
                >
                  {l.label}
                </button>
              ))}
              <button
                data-testid="nav-mobile-listen-btn"
                onClick={() => go("#platforms")}
                className="mt-2 rounded-full bg-cherry px-5 py-3 text-sm font-bold uppercase tracking-[0.14em] text-white"
              >
                Listen Now
              </button>
              <InstallButton variant="menu" />
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
}
